import { AdvancedEventEmitter, EventBroker, IEventBroker } from '@zdrbm/zdr-native-tools';
import { HeadersGetter } from '../httpClient/IHttpClientConfig';
import { IServerMessage, IServerSentEventsConsumer } from './IServerSentEventsConsumer';
import { ServerMessage } from './ServerMessage';
import { IUrl } from '../urlService';
import { IAbortController } from '../httpClient';
import { ServerSentEventError, ServerSentEventErrorTypes } from './ServerSentEventError';
import { debounce } from 'lodash';

export interface IBrowserFetchServerSentEventsConsumerOptions {
    headersGetter?: HeadersGetter;
    streamTimeout?: number;
}

const MESSAGE_END = '\n\n';
const DEFAULT_TIMEOUT_IN_MS = 10000;
export class BrowserFetchServerSentEventsConsumer
    extends AdvancedEventEmitter
    implements IServerSentEventsConsumer
{
    private reader: ReadableStreamDefaultReader<Uint8Array> | undefined = undefined;
    private lastEventId: string | undefined = undefined;
    onMessage: IEventBroker<IServerMessage>;
    onDone: IEventBroker<void>;
    private triggerTimeout: ReturnType<typeof debounce>;

    constructor(
        private consumeUrl: IUrl,
        private options: IBrowserFetchServerSentEventsConsumerOptions,
        private abortController: IAbortController,
    ) {
        super();
        this.onMessage = new EventBroker<IServerMessage>(this);
        this.onDone = new EventBroker<void>(this);
        this.triggerTimeout = debounce(
            (reject: (reason?: any) => void) => {
                this.reader?.cancel();
                reject(new ServerSentEventError(ServerSentEventErrorTypes.STREAM_TIMEOUT, undefined, 'Stream timeout'));
            },
            this.options.streamTimeout ?? DEFAULT_TIMEOUT_IN_MS,
            {
                leading: false,
                trailing: true,
            },
        );
    }

    abort(): void {
        this.abortController.abort();
    }

    async listen(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.listenImpl(reject).then(resolve);
        });
    }

    private async listenImpl(reject: (reason?: any) => void): Promise<void> {
        const headers = this.options?.headersGetter?.() || {};

        let finalUrl = this.consumeUrl;

        if (this.lastEventId) {
            finalUrl = this.consumeUrl.clone().updateParam('last_event_id', this.lastEventId);
        }

        const controller = new AbortController();
        this.abortController.onAbort.reo(() => controller.abort());
        try {
            const response = await fetch(finalUrl.toUrlString(), {
                method: 'GET',
                headers: {
                    ...headers,
                    Accept: 'text/event-stream',
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                const headers: { [key: string]: string } = {};
                response.headers.forEach((value, key) => {
                    headers[key] = value;
                });
                throw new ServerSentEventError(
                    ServerSentEventErrorTypes.HTTP_ERROR,
                    response.status.toString(),
                    response.statusText,
                    headers,
                );
            }

            this.reader = response.body!.getReader();

            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let done = false;
            let value: Uint8Array | undefined;
            let currentIndex = 0;

            while (!done) {
                this.triggerTimeout(reject);
                ({ done, value } = await this.reader.read());
                if (done) {
                    this.triggerTimeout.cancel();
                    this.onDone.emit();
                } else {
                    buffer += decoder.decode(value, { stream: true });
                    let messageEndIndex = buffer.indexOf(MESSAGE_END, currentIndex);
                    while (messageEndIndex !== -1) {
                        const message = buffer.slice(currentIndex, messageEndIndex);
                        currentIndex = messageEndIndex + MESSAGE_END.length;
                        const serverMessage = new ServerMessage(message);
                        this.lastEventId = serverMessage.getId();
                        this.onMessage.emit(serverMessage);
                        messageEndIndex = buffer.indexOf(MESSAGE_END, currentIndex);
                    }
                }
            }
        } catch (error) {
            let errorToReject = error;
            if (error instanceof Error) {
                let errorType: ServerSentEventErrorTypes | undefined = undefined;
                if (error.name === 'AbortError') {
                    errorType = ServerSentEventErrorTypes.ABORTED;
                } else if (error.name === 'TypeError') {
                    errorType = ServerSentEventErrorTypes.CONNECTION_ERROR;
                }

                if (errorType) {
                    // Some error types return a code and some do not
                    const errorAny = error as any;
                    const code = errorAny.code ? errorAny.code.toString() : undefined;
                    errorToReject = new ServerSentEventError(errorType, code, error.message);
                }
            }

            reject(errorToReject);
        }
    }
}
